import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/common/ui/Button";
import { history } from "../../_helpers";
import PodInformation from "./PodInformation";
import AccessPermission from "./AccessPermission";
import { PODAction } from "../../_store/pod.slice";
import { masterActions, authActions } from "../../_store";
import podSchema from "../../validation/podSchema";
import { useLocation } from "react-router-dom";
import KioskDetails from "./KioskDetails";
import { showToastNotification } from "../../components/common/ui/NotificationModal/ToastNotication";
import { StoredProcedures } from "../../_helpers/enumerator";
import Swal from "sweetalert2";
import CustomLoader from "../../components/common/ui/Loader";

function AddPOD() {
  // Redux hooks for dispatching actions and accessing state
  const dispatch = useDispatch();

  // Access data from Redux state using useSelector
  // const { commonFacilityCode: facilityCode } = useSelector(
  //   (state) => state.master
  // );
  const { user, commonFacilityCode } = useSelector((x) => x.auth);
  const {
    managePODdata: podData, // Destructure to avoid accessing nested properties
    specificPODdata: selectedPODData, // Renamed for better clarity
    getPODKioskList,
    InsertResponse,
    getPODList = {}, // Default to an empty object if getPODList is undefined
  } = useSelector((state) => state.pod || {}); // Single useSelector call for improved performance

  // Manage error messages in component state
  const [errorMessage, setErrorMessage] = useState({});
  const [loading, setLoading] = useState(false);
  // React Router hook to access the location object
  const location = useLocation();
  const { state } = location; // Destructure state from location

  // Retrieve the POD ID from the state, if available
  const selectedPODId = state?.id; // Use optional chaining to prevent errors if state is undefined

  // Dispatch actions to load POD information and kiosk list when selectedPODId changes
  useEffect(() => {
    if (selectedPODId) {
      setLoading(true);
      // Dispatch to get the POD information
      dispatch(PODAction.GetPODInfo(selectedPODId));

      // Dispatch to load the POD kiosk list
      dispatch(
        PODAction.LoadPODKioskList({
          PodID: selectedPODId,
          facilityID: commonFacilityCode, // Ensure correct facility ID is used
        })
      ).finally(() => setLoading(false));
    }
  }, [selectedPODId, dispatch]);

  // Dispatch action when facilityID changes in loadManagementUser
  useEffect(() => {
    if (getPODList?.facilityID) {
      // Prepare the parameters for loading the POD list
      const PODListParams = {
        ...getPODList,
        facilityID: commonFacilityCode, // Ensure correct facilityID
      };

      // Dispatch action to load the POD list and handle response
      dispatch(PODAction.LoadPODList({ PODListParams }))
        .then((response) => {
          // Optionally handle success, e.g., show notification
        })
        .catch((error) => {
          console.error("Error loading POD list:", error);
          // Optionally show a notification to the user
        });
    } else if (!commonFacilityCode) {
      // Navigate to PODList if facilityCode is not present
      history.navigate("PODList");
    }
  }, [getPODList?.facilityID, commonFacilityCode, dispatch]);

  useEffect(() => {
    const handleResponse = (message, type = "error") => {
      showToastNotification(message, type);
      dispatch(PODAction.clearResponse());
    };
    const successMessage = selectedPODId
      ? " POD Information updated successfully"
      : "POD Information saved successfully";

    switch (InsertResponse?.IsSucceed) {
      case 1:
        if (!selectedPODId) {
          // Insert Management User Activity Log
          dispatch(
            authActions?.ManagementUserActivityLog({
              ManagementUserID: user?.ManagementUserID,
              facilityID: commonFacilityCode,
              ProcedureName: StoredProcedures?.PodCPU,
              UserAction: `Created a New POD ( PODCode - ${podData?.PodCode} )`,
            })
          );
        } else {
          // Insert Management User Activity Log
          dispatch(
            authActions?.ManagementUserActivityLog({
              ManagementUserID: user?.ManagementUserID,
              facilityID: commonFacilityCode,
              ProcedureName: StoredProcedures?.PodCPU,
              UserAction: `Updated POD Details ( PODID - ${selectedPODId} )`,
            })
          );
        }
        dispatch(PODAction.resetForm());
        //dispatch(PODAction.resetSearchPOD());
        handleResponse(successMessage, "success");
        history.navigate("PODList");
        break;
      case -1:
        handleResponse("Something went wrong. Please try again.");
        break;
      case -2:
        handleResponse("The POD name already exists.");
        break;
      case -3:
        handleResponse("The POD Code already exists.");
        break;
      default:
        break;
    }
  }, [InsertResponse, dispatch, selectedPODId, history]);

  // Load facility dropdown on component mount
  useEffect(() => {
    const loadFacilities = () => {
      dispatch(
        masterActions.LoadFacilityDropDown({
          facilityID: 0,
        })
      ).catch((error) => {
        console.error("Error loading facilities:", error);
        // Optionally handle the error here (e.g., show a notification)
      });
    };

    loadFacilities();
  }, [dispatch]);

  // add POD - handleBlur validation handled here
  const handleOnBlur = (e) => {
    const { name, value } = e.target;

    const trimmedValue = value.trim();

    dispatch(
      PODAction.setPODListData({
        ...podData,
        [name]: trimmedValue,
      })
    );

    const { error } = podSchema({ ...podData, [name]: trimmedValue });

    if (error?.details) {
      const fieldError = error.details.find((e) => e.path.toString() === name);
      setErrorMessage((prev) => ({
        ...prev,
        [name]: fieldError ? fieldError.message : "",
      }));
    } else {
      setErrorMessage((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePODSave = (e) => {
    // Prevent the default form submission behavior
    e.preventDefault();

    // Clear any existing error messages before validating the form
    setErrorMessage({});

    const errorElements = document.querySelectorAll(".input-error");
    if (errorElements?.length > 0) {
      errorElements.forEach((element) =>
        element.classList.remove("input-error")
      );
    }
    // Validate podData using podSchema
    const { error } = podSchema(podData);

    // If validation errors exist
    if (error?.details) {
      // Loop through each validation error
      error.details.forEach(({ path, message }) => {
        // Set the error message for the respective field
        setErrorMessage((prev) => ({
          ...prev,
          [path]: message,
        }));

        // Find the input field with the error and add "input-error" class
        const inputElement = document.querySelector(`input[name="${path}"]`);
        if (inputElement) inputElement.classList.add("input-error");
      });

      // Stop further execution if validation errors are found
      return;
    }

    // Prepare the data to be submitted, including facilityID
    const updateFacilityID = { ...podData, facilityID: commonFacilityCode };

    // Dispatch the action to insert POD data
    dispatch(PODAction.InsertPOD(updateFacilityID));
    //dispatch(PODAction.resetForm());
  };

  const submitPODUpdate = (e) => {
    // Prevent the default form submission behavior
    e.preventDefault();

    const errorElements = document.querySelectorAll(".input-error");
    if (errorElements?.length > 0) {
      errorElements.forEach((element) =>
        element.classList.remove("input-error")
      );
    }
    // Clear any previous error messages before validating the form
    setErrorMessage({});

    // Validate podData using podSchema
    const { error } = podSchema(podData);

    // If there are validation errors
    if (error?.details) {
      // Iterate through each validation error
      error.details.forEach(({ path, message }) => {
        // Set error messages for the respective form fields
        setErrorMessage((prev) => ({
          ...prev,
          [path]: message,
        }));

        // Find the input element by the field name and add "input-error" class to highlight it
        const inputElement = document.querySelector(`input[name="${path}"]`);
        if (inputElement) inputElement.classList.add("input-error");
      });

      // Stop further execution if validation errors exist
      return;
    }

    // Add necessary properties to podData for updating, including PodID and CreatedBy
    const updatedPODData = { ...podData, PodID: selectedPODId, CreatedBy: 1 };

    // Dispatch the action to update the POD with the modified data
    dispatch(PODAction.UpdatePOD(updatedPODData));
    //dispatch(PODAction.resetForm());
  };

  const resetPODForm = (e) => {
    // Remove error styles from all input fields
    const inputWithError = document.querySelectorAll(".input-error");
    inputWithError.forEach((input) => input.classList.remove("input-error"));

    // Clear all validation error messages from the state
    setErrorMessage({});
    // If editing an existing POD, reset to specific POD data
    if (selectedPODId) {
      dispatch(PODAction.resetForm());
      dispatch(PODAction.setPODListData({ ...selectedPODData }));
      setErrorMessage({});
    } else {
      // If creating a new POD, reset the form fields
      dispatch(PODAction.resetForm());
    }
  };



  return (
    <div className="add-pod">
      <div className="d-flex flex-wrap flex-column justify-content-center align-items-center position-relative">
        <div className="card shadow-sm b-r-all  mt-3  p-4 border border-dark-grey w-100 mb-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
            <h2 className=" form_title_uppercase text-color">
              {selectedPODId ? "Edit Pod" : "Add Pod"}
            </h2>

            <Button
              label="Back"
              type="button"
              status="primary"
              icon={"fa-solid fa-angle-left"}
              onClick={() => {
                Swal.close();
                history.navigate("/Admin/PODList");
              }}
            />
          </div>
          {loading && <CustomLoader />}
          <form className="w-100">
            <PodInformation
              errorMessage={errorMessage}
              handleOnBlur={handleOnBlur}
            />
            <AccessPermission />
            {selectedPODId && getPODKioskList.length > 0 && <KioskDetails ActiveStatus={state?.ActiveStatus} />}
          </form>

          <div className="d-flex flex-wrap w-100 justify-content-center gap-3">
            <Button
              icon={
                selectedPODId
                  ? "fa-solid fa-pen-to-square"
                  : "fa-solid fa-floppy-disk"
              }
              className="success"
              label={selectedPODId ? "Update" : "Save"}
              onClick={
                selectedPODId
                  ? (e) => submitPODUpdate(e)
                  : (e) => handlePODSave(e)
              }
              status="success"
              type="submit"
            />
            <Button
              icon={"fa-solid fa-rotate-right"}
              label="Reset"
              onClick={(e) => resetPODForm(e)}
              status="secondary"
              type="button"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(AddPOD);
